import { useCallback, useEffect, useRef } from "react";
import { useDebounceCallback } from "./useDebounceCallback";
import { getSuburbRecommendation } from "services/locationService";

/**
 * Debounced Suburb -> State + PostalCode autofill lookup, centralized so every
 * Job/Lead/Contact location form wires up the same recommendation behavior.
 *
 * `onRecommendation` is invoked when a match is found (State/PostalCode filled
 * in) and also when the Suburb is cleared out (State/PostalCode blanked back
 * out) — an unrecognized, non-empty suburb simply does nothing, leaving
 * State/PostalCode for manual entry. It's read via a ref so callers can pass
 * an inline callback without breaking the debounce (useDebounceCallback
 * re-keys its timer off the function identity).
 *
 * `context` is an optional opaque value threaded straight through to
 * `onRecommendation` unchanged — for forms with more than one Suburb field
 * (e.g. repeatable contact rows), pass something identifying which field
 * triggered the lookup (e.g. its field name) so the shared debounced instance
 * can autofill the matching State/PostalCode field.
 *
 * @param {(rec: {suburb: string, state: string, postalCode: string}, context?: any) => void} onRecommendation
 * @param {number} delay - debounce delay in ms (default 300)
 * @returns {(suburb: string, context?: any) => void} lookup function — call with the current suburb text/selection. Clears are applied immediately (bypassing the debounce); non-empty lookups remain debounced.
 */
export default function useSuburbRecommendation(onRecommendation, delay = 300) {
  const onRecommendationRef = useRef(onRecommendation);
  useEffect(() => {
    onRecommendationRef.current = onRecommendation;
  }, [onRecommendation]);

  const lookup = useCallback(async (suburb, context) => {
    if (!suburb || !suburb.trim()) return;

    const result = await getSuburbRecommendation(suburb);
    if (result.success && result.data) {
      onRecommendationRef.current(result.data, context);
    }
  }, []);

  const debouncedLookup = useDebounceCallback(lookup, delay);

  const lookupOrClear = useCallback((suburb, context) => {
    if (!suburb || !suburb.trim()) {
      debouncedLookup.cancel();
      onRecommendationRef.current({ suburb: "", state: "", postalCode: "" }, context);
      return;
    }
    debouncedLookup(suburb, context);
  }, [debouncedLookup]);

  return lookupOrClear;
}
